import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiSelectBox from '../../../../../../features/components/Form/MultiSelectBox'
import FieldLabelWrapper from '../../../../../../features/components/Form/FieldLabelWrapper'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { formatAudiencesToBE } from '../../../LineItemFacebookCreate/LineItemFacebookCreateForm/formatters'
import { formatList } from '../../fields'
import { getFormattedAudiences } from '../../../../../ReusableFormFields/Audiences/FacebookAudienceFields/helper'

import { clearAudiences, getAudiences } from '../../../../../../modules/actions/audiences'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { campaignSelector } from '../../../../../../modules/selectors/campaigns'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { audiencesLoadingSelector, audiencesSelector } from '../../../../../../modules/selectors/audiences'
import { clearUpdateLineItem, updateLineItem } from '../../../../../../modules/actions/lineItems'

import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import {
  CUSTOM_AUDIENCES,
  CUSTOM_AUDIENCES_EXCLUSION
} from '../../../../../ReusableFormFields/Audiences/AudiencesFields/fields'

const CustomAudiencesSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const audiences = useSelector(audiencesSelector)
  const audiencesIsLoading = useSelector(audiencesLoadingSelector)
  const { objective: campaignObjective } = useSelector(campaignSelector)

  const formattedAudiences = useMemo(() => getFormattedAudiences(audiences), [audiences])

  const handleSubmit = useCallback(
    values => {
      const updateLineItemData = {
        account: selectedAdAccountId,
        campaign_objective: campaignObjective,
        [CUSTOM_AUDIENCES]: formatAudiencesToBE(values[CUSTOM_AUDIENCES]),
        [CUSTOM_AUDIENCES_EXCLUSION]: formatAudiencesToBE(values[CUSTOM_AUDIENCES_EXCLUSION]),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      dispatch(updateLineItem(updateLineItemData, lineItem.id, FACEBOOK_PLATFORM))
    },
    [dispatch, lineItem.id, campaignObjective, selectedAdAccountId, title]
  )

  const initialValues = useMemo(
    () => ({
      [CUSTOM_AUDIENCES]: formatList(lineItem[CUSTOM_AUDIENCES]),
      [CUSTOM_AUDIENCES_EXCLUSION]: formatList(lineItem[CUSTOM_AUDIENCES_EXCLUSION])
    }),
    [lineItem]
  )

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit
  })

  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = purifiedFormik

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  useEffect(() => {
    dispatch(
      getAudiences(
        {
          account: selectedAdAccountId,
          limit: 9999
        },
        FACEBOOK_PLATFORM
      )
    )
  }, [dispatch, selectedAdAccountId])

  useEffect(() => {
    return () => {
      dispatch(clearAudiences())
    }
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <FieldLabelWrapper label={t('Custom audience inclusions')}>
        <MultiSelectBox
          placeholder="Add an audience inclusion"
          options={formattedAudiences}
          name={CUSTOM_AUDIENCES}
          value={values[CUSTOM_AUDIENCES]}
          isLoading={audiencesIsLoading}
          setFieldValue={setFieldValue}
        />
      </FieldLabelWrapper>
      <FieldLabelWrapper label={t('Custom audience exclusions')}>
        <MultiSelectBox
          placeholder="Add an audience exclusion"
          options={formattedAudiences}
          name={CUSTOM_AUDIENCES_EXCLUSION}
          value={values[CUSTOM_AUDIENCES_EXCLUSION]}
          isLoading={audiencesIsLoading}
          setFieldValue={setFieldValue}
        />
      </FieldLabelWrapper>
    </EditForm>
  )
}

export default CustomAudiencesSectionForm
